import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, UserCircle, Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Select, Card } from "@/components/ui";
import { usersApi, clientsApi } from "@/services/api";
import { useEmailValidation } from "@/hooks";

interface UserFormData {
  name: string;
  email: string;
  phone: string;
  role: "admin" | "manager" | "seller" | "client";
  client_id?: string;
  is_active: boolean;
  password?: string;
  confirm_password?: string;
}

export default function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      role: "seller",
      client_id: "",
      is_active: true,
      password: "",
      confirm_password: "",
    },
  });

  const selectedRole = watch("role");
  const password = watch("password");
  const email = watch("email");

  // Email validation hook (only for new users, disabled for editing)
  const emailValidation = useEmailValidation(email);

  // Carregar dados do usuário (edição)
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersApi.get(id!),
    enabled: isEditing,
  });

  // Carregar clientes (para o select)
  const { data: clientsData } = useQuery({
    queryKey: ["clients", "all"],
    queryFn: () => clientsApi.list({ page: 1, per_page: 1000 }),
    enabled: selectedRole === "client",
  });

  useEffect(() => {
    if (userData?.data) {
      const user = userData.data;
      reset({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: user.role || "seller",
        client_id: user.client_id || "",
        is_active: Boolean(user.is_active),
        password: "",
        confirm_password: "",
      });
    }
  }, [userData, reset]);

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      toast.success("Usuário criado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate("/admin/users");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao criar usuário");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserFormData) => usersApi.update(id!, data),
    onSuccess: () => {
      toast.success("Usuário atualizado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      navigate("/admin/users");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao atualizar usuário");
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: UserFormData) => {
    // Validações
    if (!isEditing && !data.password) {
      toast.error("Senha é obrigatória para novos usuários");
      return;
    }

    if (data.password && data.password !== data.confirm_password) {
      toast.error("As senhas não conferem");
      return;
    }

    if (data.password && data.password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    // Remover confirm_password antes de enviar
    const { confirm_password, ...submitData } = data;

    // Não enviar client_id se não for cliente
    if (submitData.role !== "client") {
      submitData.client_id = undefined;
    }

    // Não enviar senha vazia em edição
    if (isEditing && !submitData.password) {
      delete submitData.password;
    }

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  if (isEditing && isLoadingUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const clients = clientsData?.data?.items || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/users")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <UserCircle className="w-8 h-8 text-primary" />
            {isEditing ? "Editar Usuário" : "Novo Usuário"}
          </h1>
          <p className="text-text-secondary mt-1">
            {isEditing
              ? "Atualize as informações do usuário"
              : "Cadastre um novo usuário no sistema"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Informações Básicas */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Informações Básicas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Completo *"
              format="name"
              {...register("name", { required: "Nome é obrigatório" })}
              error={errors.name?.message}
            />
            <Input
              label="Email *"
              type="email"
              format="email"
              {...register("email", {
                required: "Email é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email inválido",
                },
              })}
              error={errors.email?.message}
              disabled={isEditing}
              hint={
                isEditing
                  ? "Email não pode ser alterado"
                  : emailValidation.isValidating
                    ? "Validando..."
                    : emailValidation.message
              }
              className={
                !isEditing && emailValidation.valid === true
                  ? "border-green-500"
                  : !isEditing && emailValidation.valid === false
                    ? "border-red-500"
                    : ""
              }
            />
            <Input
              label="Telefone"
              format="phone"
              {...register("phone")}
              placeholder="(00) 00000-0000"
            />
            <Select
              label="Função *"
              {...register("role", { required: "Função é obrigatória" })}
              options={[
                { value: "admin", label: "Administrador" },
                { value: "manager", label: "Gerente" },
                { value: "seller", label: "Vendedor" },
                { value: "client", label: "Cliente" },
              ]}
              error={errors.role?.message}
            />
            {selectedRole === "client" && (
              <div className="md:col-span-2">
                <Select
                  label="Cliente Vinculado"
                  {...register("client_id")}
                  options={[
                    { value: "", label: "Selecione um cliente..." },
                    ...clients.map((client: any) => ({
                      value: client.id,
                      label: client.name,
                    })),
                  ]}
                  hint="Vincule este usuário a um cliente cadastrado"
                />
              </div>
            )}
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("is_active")}
                  className="checkbox"
                />
                <div>
                  <span className="text-text-primary font-medium">
                    Usuário Ativo
                  </span>
                  <p className="text-sm text-text-muted">
                    Usuários inativos não podem fazer login no sistema
                  </p>
                </div>
              </label>
            </div>
          </div>
        </Card>

        {/* Senha */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            {isEditing ? "Alterar Senha (opcional)" : "Senha *"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={isEditing ? "Nova Senha" : "Senha *"}
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: !isEditing ? "Senha é obrigatória" : false,
                minLength: {
                  value: 6,
                  message: "A senha deve ter no mínimo 6 caracteres",
                },
              })}
              error={errors.password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="cursor-pointer"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              }
              hint={
                isEditing
                  ? "Deixe em branco para manter a senha atual"
                  : undefined
              }
            />
            <Input
              label="Confirmar Senha"
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirm_password", {
                validate: (value) =>
                  !password || value === password || "As senhas não conferem",
              })}
              error={errors.confirm_password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="cursor-pointer"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              }
            />
          </div>
        </Card>

        {/* Ações */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/users")}
          >
            Cancelar
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? "Salvar Alterações" : "Cadastrar Usuário"}
          </Button>
        </div>
      </form>
    </div>
  );
}
